import {FC} from 'react'
import {Content} from '@prismicio/client'
import {PrismicRichText, SliceComponentProps} from '@prismicio/react'
import {Gradient, CenterSeparators, SideSeparators} from './components/separators'
import {ProductOverviewCard} from './components/ProductCard'
import {Context} from 'src/common/types'

/**
 * Props for `ProductOverview`.
 */
export type ProductOverviewProps = SliceComponentProps<Content.ProductOverviewSlice> & {context: Context}

/**
 * Component for "ProductOverview" Slices.
 */
const ProductOverview: FC<ProductOverviewProps> = ({slice, context, index: componentIndex}) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="flex flex-col gap-12 bg-moss-white pb-0 pt-20"
    >
      <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6 px-5 md:px-10 lg:px-5 xl:px-0">
        <PrismicRichText
          field={slice.primary.tag}
          components={{
            paragraph: ({children}) => <p className="text-button-s font-semibold text-moss-grey-400">{children}</p>,
            strong: ({children}) => <strong className="pr-3 font-semibold text-moss-blue">{children}</strong>,
          }}
        />
        <div>
          <PrismicRichText
            field={slice.primary.heading}
            components={{
              heading1: ({children}) => (
                <h1 className="md:text-tg-h1 text-[32px] font-normal leading-9 tracking-[-.32px] text-moss-grey-400">
                  {children}
                </h1>
              ),
              strong: ({children}) => (
                <>
                  <span className="text-black">{children}</span>
                  <br className="max-lg:hidden" />
                </>
              ),
            }}
          />
        </div>
      </div>
      <div
        className="
      relative
      isolate
      grid
      grid-cols-[20px_1fr_20px]
      grid-rows-[40px_auto_20px_auto_20px_auto_20px_auto_20px_auto_20px_auto_40px]
      md:grid-cols-[20px_20px_1fr_24px_1fr_20px_20px]
      md:grid-rows-[64px_minmax(324px,_auto)_48px_minmax(324px,_auto)_48px_minmax(324px,_auto)_80px]
      lg:grid-cols-[20px_minmax(0,385px)_24px_minmax(0,385px)_24px_minmax(0,385px)_20px]
      lg:grid-rows-[64px_1fr_48px_1fr_80px]
      xl:grid-cols-[1fr_60px_minmax(0,385px)_24px_minmax(0,385px)_24px_minmax(0,385px)_60px_1fr]
      "
      >
        <Gradient color="255, 255, 255" gradientStart={0} height={24} location="top" />
        {slice.primary.product_sections.map((content, index) => (
          <ProductOverviewCard
            content={content}
            index={index}
            key={index}
            componentIndex={componentIndex}
            pageUid={context.pageUid}
          />
        ))}

        <CenterSeparators />
        <SideSeparators />
        <Gradient color="255, 255, 255" gradientStart={20} height={32} location="bottom" />
      </div>
    </section>
  )
}

export default ProductOverview
